import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { ChevronRight, ChevronDown, History } from 'lucide-react';
import { useUsersData } from '../../Helpers/UsersContext';
import {
    useArchiveHistory,
    DEFAULT_HISTORY_LIMIT,
} from '../../hooks/archive/useArchiveHistory';
import {
    HistoryGroupBlock,
    createUserMap,
} from '../../Components/History/HistoryComponents';
import './LevelHistorySection.css';

// ── Props ───────────────────────────────────────────────────────────

interface LevelHistorySectionProps {
    buildingKey: string;
    levelKey: string;
    roomKeys: string[];  // Keys of rooms on this level for nested room history
}

// ── Component ───────────────────────────────────────────────────────

const LevelHistorySection: React.FC<LevelHistorySectionProps> = ({
    buildingKey,
    levelKey,
    roomKeys,
}) => {
    const { users, loadingUsers } = useUsersData();
    const [isExpanded, setIsExpanded] = useState(false);
    const [showRoomHistory, setShowRoomHistory] = useState(false);

    // O(1) user lookup map
    const userMap = useMemo(() => createUserMap(users), [users]);

    // Load level history
    const {
        loading: levelLoading,
        error: levelError,
        groupedHistory: levelHistory,
    } = useArchiveHistory(buildingKey, levelKey, 'levels');

    // Load room history for rooms on this level
    const {
        loading: roomLoading,
        groupedHistory: allRoomHistory,
    } = useArchiveHistory(buildingKey, undefined, 'rooms');

    // Filter room history to only rooms on this level
    const roomHistory = useMemo(() => {
        if (!roomKeys.length) return [];
        const roomKeySet = new Set(roomKeys);
        return allRoomHistory.map(group => ({
            ...group,
            entries: group.entries.filter(entry => roomKeySet.has(entry.entityKey)),
        })).filter(group => group.entries.length > 0);
    }, [allRoomHistory, roomKeys]);

    const loading = levelLoading || roomLoading;
    const hasLevelChanges = levelHistory.length > 0;
    const hasRoomChanges = roomHistory.length > 0;
    const hasAnyHistory = hasLevelChanges || hasRoomChanges;

    const totalLevelChanges = levelHistory.reduce(
        (sum, group) => sum + group.entries.reduce((s, e) => s + e.changes.length, 0),
        0
    );
    const totalRoomChanges = roomHistory.reduce(
        (sum, group) => sum + group.entries.reduce((s, e) => s + e.changes.length, 0),
        0
    );

    const handleKeyDown = (e: React.KeyboardEvent, toggle: () => void) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
        }
    };

    return (
        <div className="level-history-section">
            <div
                className="level-history-header"
                role="button"
                tabIndex={0}
                onClick={() => setIsExpanded(!isExpanded)}
                onKeyDown={(e) => handleKeyDown(e, () => setIsExpanded(!isExpanded))}
            >
                <span className="level-history-chevron">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </span>
                <History size={18} className="level-history-icon" />
                <span className="level-history-title">Historik</span>
                {!loading && hasAnyHistory && (
                    <span className="level-history-badge">
                        {totalLevelChanges + totalRoomChanges} ändringar
                    </span>
                )}
            </div>

            {isExpanded && (
                <div className="level-history-body">
                    {loading && (
                        <div className="level-history-loading">
                            <Spinner animation="border" size="sm" />
                            <span>Laddar historik...</span>
                        </div>
                    )}

                    {levelError && (
                        <p className="text-danger">{levelError}</p>
                    )}

                    {!loading && !hasAnyHistory && (
                        <p className="text-muted">Inga ändringar har registrerats</p>
                    )}

                    {/* Level changes */}
                    {!loading && hasLevelChanges && (
                        <div className="level-history-group">
                            <h5 className="level-history-group-title">Våningsplansändringar</h5>
                            {levelHistory.slice(0, DEFAULT_HISTORY_LIMIT).map(group => (
                                <HistoryGroupBlock
                                    key={group.groupKey}
                                    group={group}
                                    userMap={userMap}
                                    loadingUsers={loadingUsers}
                                />
                            ))}
                            {levelHistory.length > DEFAULT_HISTORY_LIMIT && (
                                <p className="text-muted level-history-more">
                                    +{levelHistory.length - DEFAULT_HISTORY_LIMIT} fler dagar
                                </p>
                            )}
                        </div>
                    )}

                    {/* Room changes (collapsible) */}
                    {!loading && hasRoomChanges && (
                        <div className="level-history-group">
                            <div
                                className="level-history-room-header"
                                role="button"
                                tabIndex={0}
                                onClick={() => setShowRoomHistory(!showRoomHistory)}
                                onKeyDown={(e) => handleKeyDown(e, () => setShowRoomHistory(!showRoomHistory))}
                            >
                                <span className="level-history-chevron">
                                    {showRoomHistory ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </span>
                                <span className="level-history-group-title">
                                    Rumsändringar ({totalRoomChanges})
                                </span>
                            </div>

                            {showRoomHistory && (
                                <div className="level-history-room-body">
                                    {roomHistory.slice(0, DEFAULT_HISTORY_LIMIT).map(group => (
                                        <HistoryGroupBlock
                                            key={group.groupKey}
                                            group={group}
                                            userMap={userMap}
                                            loadingUsers={loadingUsers}
                                        />
                                    ))}
                                    {roomHistory.length > DEFAULT_HISTORY_LIMIT && (
                                        <p className="text-muted level-history-more">
                                            +{roomHistory.length - DEFAULT_HISTORY_LIMIT} fler dagar
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LevelHistorySection;
